import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Menu, MenuButton, MenuList, MenuItem, HStack, Text, VStack, useDisclosure } from '@chakra-ui/react'
import SvgIcon from '../SvgIcon'
import logo from 'assets/images/howfactory.png'
import logoSm from 'assets/images/logo.png'
import classNames from 'classnames'
import cx from 'classnames'
import config from 'configs'
import { useStores } from 'hooks/useStores'
import { observer } from 'mobx-react'
import { Link, matchPath, useNavigate, useLocation } from 'react-router-dom'
import SimpleBar from 'simplebar-react'
import 'simplebar-react/dist/simplebar.min.css'
import colors from 'theme/colors.theme'
import { AuthRoleIdEnum } from 'constants/user'
import { IGroupMemberPermissionId } from 'interfaces/group'
import { ITheme } from 'interfaces/theme'
import routes from 'routes'
import { canUserCreateCollections } from 'utils/permissions'
import { tertiary } from '../../theme/globalStyles'
import SideBarContent from './components/SideBarContent'
import styles from './leftSidebar.module.scss'
import FeedbackModal from './components/FeedbackModal'
import CurrentVersionModal from './components/CurrentVersionModal'
import useFeatureFlagValue from 'hooks/useFeatureFlagValue'
import { EFeatureFlags } from 'constants/featureFlags'
interface ILeftSidebarProps {
  menuClickHandler?: {}
  hideLogo?: boolean
  isCondensed: boolean
  showSidebarMobile: boolean
  setShowSidebarMobile: (showSidebarMobile: boolean) => void
}

const LeftSidebar = (props: ILeftSidebarProps) => {
  const isCondensed = props.isCondensed || false
  const location = useLocation()
  const [showFeedbackModal, setShowFeedbackModal] = useState<boolean>(false)
  const { isOpen: isVersionModalOpen, onOpen: onVersionModalOpen, onClose: onVersionModalClose } = useDisclosure()
  const { searchStore, userStore, authStore, organizationStore } = useStores()
  const { organization } = organizationStore
  const horizonTalImage: string = organization?.horizontalLogo ?? ''
  const squareImage: string = organization?.logo ?? ''
  const currentTheme: ITheme = organization?.theme ?? {}
  const isLightSideBar: boolean = organization?.isLightSideBar ?? false
  const showSidebarMobile = props.showSidebarMobile || false
  const hideLogo = props.hideLogo || false
  const { currentUserGroupMembers } = userStore
  const isBasicUser =
    !authStore?.userDetail?.authRoleId || authStore?.userDetail?.authRoleId === AuthRoleIdEnum.BASIC_USER
  const userCanManage =
    currentUserGroupMembers?.some(groupMember => groupMember?.permissionId === IGroupMemberPermissionId.EDITOR) ||
    !isBasicUser
  const userCanCreateCollections = canUserCreateCollections(authStore.userDetail)
  const userCanCreateDrafts = userCanManage // Same logic as used in other components

  // Feature flag for release notes
  const { value: isReleaseNotesEnabled } = useFeatureFlagValue(EFeatureFlags.RELEASE_NOTES, true)

  const isMatchedDashboardPage = !!matchPath(routes.dashboard.value, location?.pathname)
  const isMatchedSearchPage = !!matchPath(routes.search.value, location?.pathname)
  const isManageModeInSearchPage = isMatchedSearchPage && searchStore?.isManageMode

  const menuNodeRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const handleClickOutSide = useCallback(
    event => {
      if (!menuNodeRef?.current?.contains(event?.target) && showSidebarMobile) {
        props.setShowSidebarMobile(false)
      }
    },
    [showSidebarMobile, menuNodeRef]
  )
  useEffect(() => {
    document.addEventListener('click', handleClickOutSide, true)
    return () => {
      document.removeEventListener('click', handleClickOutSide, true)
    }
  }, [handleClickOutSide])

  const handleCreateDraft = () => {
    navigate(routes.processes.value, {
      state: {
        showCreateProcessDraftDialog: true
      }
    })
  }

  const handleCollection = () => {
    navigate('/collections', {
      state: {
        showCreateCollectionDialog: true
      }
    })
  }

  return (
    <div
      style={{ backgroundColor: !isLightSideBar ? (currentTheme?.tertiaryColor ?? tertiary) : colors.gray[200] }}
      className={classNames(styles.leftSideMenu, 'left-side-menu', props.showSidebarMobile ? 'showSidebarMobile' : '')}
      ref={menuNodeRef}
    >
      <div>
        {!hideLogo && (
          <React.Fragment>
            <Link to="/" className="logo text-center logo-light">
              <span
                className="logo-lg"
                style={{
                  backgroundColor: !isLightSideBar ? (currentTheme?.tertiaryColor ?? tertiary) : colors.gray[200]
                }}
              >
                <img
                  src={horizonTalImage || logo}
                  style={{ marginRight: '20px', width: '100%', maxWidth: '212px', objectFit: 'contain' }}
                  alt="logo"
                  height="30"
                />
              </span>
              <span
                className="logo-sm"
                style={{
                  backgroundColor: !isLightSideBar ? (currentTheme?.tertiaryColor ?? tertiary) : colors.gray[200]
                }}
              >
                <img
                  src={squareImage || logoSm}
                  style={{ height: '30px', width: '30px', objectFit: 'contain' }}
                  alt="logo"
                  height="16"
                />
              </span>
            </Link>
          </React.Fragment>
        )}

        {!isCondensed ? (
          <SimpleBar style={{ maxHeight: '100%' }} timeout={500} scrollbarMaxSize={320}>
            <SideBarContent isCondensed={isCondensed} isBasicUser={isBasicUser} haveEditorRole={userCanManage} />
          </SimpleBar>
        ) : (
          <SideBarContent isCondensed={isCondensed} isBasicUser={isBasicUser} haveEditorRole={userCanManage} />
        )}
      </div>

      {(isMatchedDashboardPage || isManageModeInSearchPage) && (userCanCreateDrafts || userCanCreateCollections) && (
        <div>
          <Menu>
            <MenuButton
              as={Button}
              className={cx({
                [styles.buttonCreateCondensed]: isCondensed,
                [styles.buttonCreate]: !isCondensed
              })}
            >
              <SvgIcon size={24} iconName="icon-create" />
            </MenuButton>
            <MenuList>
              {userCanCreateDrafts && (
                <MenuItem
                  fontSize="16px"
                  fontWeight="400"
                  lineHeight="24px"
                  color="gray.700"
                  background="white"
                  border="unset"
                  boxShadow="unset"
                  icon={<SvgIcon iconName="draft-icon" size={16} />}
                  onClick={handleCreateDraft}
                >
                  Create draft
                </MenuItem>
              )}
              {userCanCreateCollections && (
                <MenuItem
                  fontSize="16px"
                  fontWeight="400"
                  lineHeight="24px"
                  color="gray.700"
                  alignItems="center"
                  background="white"
                  border="unset"
                  boxShadow="unset"
                  icon={<SvgIcon iconName="ic-collection" size={16} />}
                  onClick={handleCollection}
                >
                  Create collection
                </MenuItem>
              )}
            </MenuList>
          </Menu>
        </div>
      )}

      <VStack spacing={2} paddingTop={4}>
        {!isCondensed && (
          <Button
            variant="outline"
            border={0}
            minHeight={10}
            background="whiteAlpha.200"
            width="calc(100% - 40px)"
            _hover={{ background: 'whiteAlpha.300' }}
            onClick={() => setShowFeedbackModal(true)}
          >
            <HStack width="full" justify="space-between">
              <Text color="gray.200" fontSize="sm" fontWeight={500} lineHeight={5}>
                Give feedback
              </Text>
              <SvgIcon iconName="ic_baseline-edit" color="white" size={20} />
            </HStack>
          </Button>
        )}
        {isReleaseNotesEnabled ? (
          <div className={styles.versionWrapper}>
            <p 
              className="version-condensed" 
              onClick={userCanManage ? onVersionModalOpen : undefined}
              style={{ cursor: userCanManage ? 'pointer' : 'default' }}
            >
              App version:
            </p>
            <p>{config.version}</p>
          </div>
        ) : (
          <div className={styles.versionWrapper}>
            <p className="version-condensed">
              App version:
            </p>
            <p>{config.version}</p>
          </div>
        )}
      </VStack>
      <FeedbackModal isOpen={showFeedbackModal} onClose={() => setShowFeedbackModal(false)} />
      {isReleaseNotesEnabled && (
        <CurrentVersionModal isOpen={isVersionModalOpen} onClose={onVersionModalClose} />
      )}
    </div>
  )
}

export default observer(LeftSidebar)
